// 七個頁型模板共用的小工具與微元件。
//
// ponytail: 只收「七個模板裡真的重複出現、而且行為完全相同」的東西
// （ID 正規化、錯誤框的 variant 判斷、欄位控制項對應）。
// 頁型之間**行為不同**的部分（查詢／表單／唯讀／轉址說明）刻意留在各自的檔案裡，
// 不抽成帶一堆 boolean props 的萬用頁面元件 —— §9.5 明文禁止。

import type { ReactElement, ReactNode } from 'react';
import type { RouteEntry } from '@/app/routes.generated';
import { Select } from '@/components/base/Select';
import { Textarea } from '@/components/base/Textarea';
import { TextInput } from '@/components/base/TextInput';
import { DialogProvider, ErrorFrame } from '@/components/layouts';
import { type columnsFor, MockDataError, type Row, resolveLegacyMessageForAction } from '@/mocks';
import { formSpecFor } from '@/mocks/legacy-form-specs';

/**
 * 批次 36（GNL-005-b，Gitea issue #38）：legacy `<form>` 包裝層鏡像。
 *
 * legacy 查詢/參數區是 `<form>`（送出語意載體）；泛用模板／body 只包 div 時
 * diff 會標「缺元素 form」。裁決（使用者 2026-09-30）：**補齊 form（分段）**——延伸
 * SearchPanel「刻意保留原生 form onSubmit」決策。attr 由 LEGACY_FORM_SPECS 鏡像
 * （normalize ATTR_ALLOW 含 action/method/target、不含 class/id/onsubmit →
 * 寫死同值即 diff 等值；mock-only 不真送出，onSubmit preventDefault）。
 */
export function FormWrap(props: { pageId: string; children: ReactNode }): ReactElement {
  const spec = formSpecFor(props.pageId);
  // 白名單制：未登錄（R-diff verdict SKIP 群）＝不包，維持原 div 平鋪。
  if (spec === undefined) {
    return <>{props.children}</>;
  }
  return (
    <form
      data-ui-id={`${props.pageId}-query-form`}
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
      }}
      {...(spec.action === undefined ? {} : { action: spec.action })}
      {...(spec.method === undefined ? {} : { method: spec.method })}
      {...(spec.target === undefined ? {} : { target: spec.target })}
    >
      {props.children}
    </form>
  );
}

/**
 * 欄位定義。刻意用 `ReturnType<typeof columnsFor>[number]` 取型別，而不是
 * `import type { Column } from '@/mocks/fixtures'` —— 頁面不得直接 import fixture 檔
 * （@/mocks/index.ts 的硬性規則），而 barrel 沒有把 Column 這個名字匯出。
 * 【第二層／假資料層契約不足 #1】：建議 @/mocks 補匯出 `Column` 型別。
 */
export type PageColumn = ReturnType<typeof columnsFor>[number];

/**
 * 把任意字串轉成 data-ui-id 可用的 kebab-case（§10.1：全小寫、連字號分隔）。
 *
 * 兩個實際會遇到的來源：
 *   - fixture 的欄位 key 是 camelCase（`createdAt` → `created-at`）
 *   - fixture 的列編號含大寫與連字號（`ANN-114-0001` → `ann-114-0001`）
 */
export function toUiKey(value: string): string {
  return value
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replace(/[^A-Za-z0-9]+/g, '-')
    .toLowerCase()
    .replace(/^-+|-+$/g, '');
}

/** 這一列的 data-ui-id 片段。fixture 的 id 欄位就是 §10.1 說的「固定 mock fixture key」。 */
export function rowUiKey(row: Row, index: number): string {
  const id = row.id;
  return id === undefined || id === null ? `index-${String(index)}` : toUiKey(String(id));
}

/** 把一列資料轉成表單用的字串值（受控元件一律吃 string）。 */
export function toFormValues(
  columns: readonly PageColumn[],
  row: Row | null,
): Record<string, string> {
  const values: Record<string, string> = {};
  for (const column of columns) {
    const raw = row === null ? undefined : row[column.key];
    values[column.key] = raw === undefined || raw === null ? '' : String(raw);
  }
  return values;
}

/** unauthorized 與一般錯誤要顯示不同文案（§5.2 的狀態需求）。 */
export function isUnauthorized(error: unknown): boolean {
  return error instanceof MockDataError && error.code === 'unauthorized';
}

/** validation-error 時 DataSource 會把欄位錯誤放在 MockDataError.fields。 */
export function fieldErrorsOf(cause: unknown): Record<string, string> {
  return cause instanceof MockDataError ? { ...cause.fields } : {};
}

/**
 * FC-5 R3（C1-c″）：這頁這個分類的 legacy validation 訊息數超過可用欄位數時，
 * 多出來的訊息（多槽位顯示，見 `MockDataError.extraMessages`）。
 */
export function extraMessagesOf(cause: unknown): readonly string[] {
  return cause instanceof MockDataError ? cause.extraMessages : [];
}

/**
 * FC-5 R3（C1-c″）：這頁這個分類的 legacy validation 訊息數超過表單可用欄位數時，
 * 多出來的訊息在這裡集中顯示——每個欄位各自的錯誤已經顯示在欄位旁，這份清單補上
 * 「欄位數不夠裝」的剩餘訊息，讓 91 條裡屬 validation 的訊息都能真的被畫出來，
 * 不是為了湊分子而畫的裝飾清單。
 */
export function ExtraValidationMessages({
  uiId,
  messages,
}: {
  uiId: string;
  messages: readonly string[];
}) {
  if (messages.length === 0) return null;
  return (
    <ul data-ui-id={`${uiId}-extra-errors`} className="list-disc space-y-1 pl-5 text-danger text-sm">
      {messages.map((message, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: 訊息本身可能重複，位置本身就是穩定的識別依據。
        <li key={index} data-ui-id={`${uiId}-extra-error-${index}`}>
          {message}
        </li>
      ))}
    </ul>
  );
}

export interface TemplateErrorProps {
  uiId: string;
  error: Error;
  onRetry?: () => void;
  /** FC-5 R2（C1-c′）：有值時，非 unauthorized 的錯誤會改顯示這頁的 legacy failure 訊息。 */
  pageId?: string;
  /** FC-5 R4（C1-c‴）：同頁同動作多條 failure 訊息碰撞時，指定要顯示哪一條。 */
  messageKeyOverride?: string;
}

/**
 * 錯誤／無權限的整區塊顯示。
 * unauthorized 走 ErrorFrame 的 unauthorized variant，其預設標題就是 `t('error.forbiddenTitle')`（FC-5 Q3 拆三 key 逐字對齊 legacy）。
 * 一般 error variant 預設仍是 `t('error.generic')`；若呼叫端帶 `pageId` 且該頁有 legacy 的
 * failure 訊息，改顯示那條逐字文字（C1-c′ 要求 React 側 texts 必須能被實機互動觸發）。
 */
export function TemplateError({ uiId, error, onRetry, pageId, messageKeyOverride }: TemplateErrorProps) {
  const unauthorized = isUnauthorized(error);
  // FC-5 R3：這是資料讀取失敗（list/get 的 query 動作），不是寫入動作，用 'query' 關鍵字。
  const legacyFailure =
    !unauthorized && pageId !== undefined
      ? resolveLegacyMessageForAction(pageId, 'failure', 'query', messageKeyOverride)
      : null;
  return (
    <ErrorFrame
      uiId={uiId}
      variant={unauthorized ? 'unauthorized' : 'error'}
      {...(legacyFailure === null ? {} : { title: legacyFailure.text })}
      {...(onRetry === undefined ? {} : { onRetry })}
    />
  );
}

export interface FieldControlProps {
  uiId: string;
  column: PageColumn;
  value: string;
  onChange: (next: string) => void;
  /** 唯讀（view-only 狀態、以及編號欄）。Select 沒有 readOnly，改用 disabled。 */
  readOnly?: boolean;
  invalid?: boolean;
  /** 覆寫 status 欄位的可選值；沒傳就用欄位定義自帶的 pool。 */
  options?: readonly string[];
  /**
   * crud-fix（2026-10-04，P35 反轉）：id（編號）欄解除唯讀的例外。
   * config-job-title 的「職稱代碼」欄 legacy showModal 新增/修改皆可填
   * （jobTitle.jsp:12/:23 `$('#code').val(...)`、save() :126-131 code required）。
   * 未傳（undefined→false）＝原行為：id 欄一律唯讀（courseDepartmentCode.jsp:108）。
   */
  allowIdEdit?: boolean;
}

/**
 * 依欄位 kind 選控制項。
 *
 * ponytail: 只有三種控制項（下拉／多行／單行），不做 schema 驅動的欄位工廠 ——
 * 雛形的欄位形狀來自 fixtures 的四種 shape，等 Gate 4 各模組欄位定案後，
 * 由該 feature 的第三層元件覆寫，本函式不需要成長。
 */
export function FieldControl({
  uiId,
  column,
  value,
  onChange,
  readOnly = false,
  invalid = false,
  options,
  allowIdEdit = false,
}: FieldControlProps) {
  const pool = options ?? column.pool;
  if (column.kind === 'status' && pool !== undefined) {
    return (
      <Select
        uiId={uiId}
        value={value}
        onChange={onChange}
        disabled={readOnly}
        invalid={invalid}
        options={pool.map((item) => ({ value: item, label: item }))}
      />
    );
  }
  if (column.kind === 'text') {
    return (
      <Textarea
        uiId={uiId}
        value={value}
        onChange={onChange}
        readOnly={readOnly}
        invalid={invalid}
      />
    );
  }
  // 編號欄一律唯讀：原系統的 id 是 hidden input（courseDepartmentCode.jsp:108），
  // 雛形改成看得見但不可改，讓「這一筆是誰」在畫面上有跡可循。
  // 例外（crud-fix，P35 反轉）：行為層 modalIdEditable 的頁（config-job-title code 欄）
  // legacy showModal 新增/修改皆可填，allowIdEdit 解除唯讀。
  return (
    <TextInput
      uiId={uiId}
      value={value}
      onChange={onChange}
      readOnly={readOnly || (column.kind === 'id' && !allowIdEdit)}
      invalid={invalid}
    />
  );
}

export interface DialogHostProps {
  route: RouteEntry;
  children: ReactNode;
}

/**
 * 把 <DialogProvider> 包在模板自己身上。
 *
 * 為什麼不直接用路由層 AppLayout 的那一層：useDialogs() 是在模板元件**本身**呼叫的，
 * Provider 必須在它外面，模板沒辦法自己包住自己。自帶一層有兩個好處：
 *   1. 模板可以單獨 render（單元測試、Storybook）而不必先架整個 AppLayout；
 *   2. 對話框的 data-ui-id 帶得到 pageId 前綴（§10.1），而不是全站共用的 `global-dialog-*`。
 * 與 AppLayout 的 <DialogProvider> 巢狀不衝突：內層優先，且對話框關閉時完全不 render DOM，
 * 不會產生重複的 data-ui-id。
 * 【與路由層待收斂 #1】若之後決定全站只留一組對話框，把這個元件拿掉即可，模板內文不用改。
 *
 * uiId 用 pageId 當前綴（§10.1），所以確認框是 `{pageId}-dialog-confirm`。
 */
export function DialogHost({ route, children }: DialogHostProps) {
  return <DialogProvider uiId={`${route.pageId}-dialog`}>{children}</DialogProvider>;
}
